/**
 * 서비스 기간 관련 유틸리티 함수들
 */

/**
 * 서비스 만료일까지 남은 일수를 계산합니다.
 * @param expiresAt 서비스 만료일 (Date 또는 ISO 문자열)
 * @returns 남은 일수 (양수: 남은 일수, 0: 오늘 만료, 음수: 만료됨)
 */
export function calculateDaysUntilExpiry(expiresAt: Date | string | null): number {
  if (!expiresAt) return -1; // 만료일이 없으면 만료된 것으로 처리
  
  const expiryDate = typeof expiresAt === 'string' ? new Date(expiresAt) : expiresAt;
  const now = new Date();
  
  // 시간을 00:00:00으로 정규화하여 일 단위로 계산
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const expiry = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate());
  
  const diffTime = expiry.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
}

/**
 * 서비스 만료 상태를 확인합니다.
 * @param expiresAt 서비스 만료일 (Date 또는 ISO 문자열)
 * @returns 만료 여부
 */
export function isServiceExpired(expiresAt: Date | string | null): boolean {
  return calculateDaysUntilExpiry(expiresAt) < 0;
}

/**
 * 서비스 기간 텍스트를 반환합니다.
 * @param expiresAt 서비스 만료일 (Date 또는 ISO 문자열)
 * @returns 서비스 기간 텍스트
 */
export function getServicePeriodText(expiresAt: Date | string | null): string {
  const daysLeft = calculateDaysUntilExpiry(expiresAt);
  
  if (daysLeft < 0) {
    return '만료됨';
  } else if (daysLeft === 0) {
    return '0일';
  } else {
    return `${daysLeft}일`;
  }
}

/**
 * 서비스 기간에 따른 색상을 반환합니다.
 * @param expiresAt 서비스 만료일 (Date 또는 ISO 문자열)
 * @returns Tailwind CSS 색상 클래스
 */
export function getServicePeriodColor(expiresAt: Date | string | null): string {
  const daysLeft = calculateDaysUntilExpiry(expiresAt);
  
  if (daysLeft < 0) {
    return 'text-red-500'; // 만료됨
  } else if (daysLeft === 0) {
    return 'text-red-500'; // 오늘 만료
  } else if (daysLeft <= 14) {
    return 'text-red-500'; // 14일 이내 - 붉은색
  } else if (daysLeft <= 30) {
    return 'text-yellow-500'; // 1개월 이내
  } else {
    return 'text-green-500'; // 충분한 기간
  }
}

/**
 * 서비스 만료 임박 여부를 확인합니다 (14일 이내).
 * @param expiresAt 서비스 만료일 (Date 또는 ISO 문자열)
 * @returns 만료 임박 여부
 */
export function isServiceExpiringSoon(expiresAt: Date | string | null): boolean {
  const daysLeft = calculateDaysUntilExpiry(expiresAt);
  return daysLeft >= 0 && daysLeft <= 14;
}
